/**
 * Phase 15.1 — Optimisation campaign detail page.
 *
 * Renders:
 *   • Status badge + GA progress chip (current / total generations).
 *   • Convergence chart — Plotly line plot of best Pk + mean Pk per
 *     generation. Reads `campaign.convergence_trace`.
 *   • Best individual table — gene values vs registry bounds.
 *   • Cancel button when status === "running" (or "queued").
 *   • Placeholder card for the future 3D viewer (15.4) carrying the
 *     PM-required testid `optimisation-detail-convergence-3d-card-placeholder`.
 *
 * The page polls every 4 s while the campaign is still running so
 * the operator sees live progress without page refresh.
 */
import { useEffect, useMemo, useRef, useState, lazy, Suspense } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, Loader2, AlertTriangle, X, Sparkles, FileDown, Zap, Brain } from "lucide-react";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { scrubPhaseLabel } from "@/lib/displayName";
import { api as client } from "@/api/client";
import optimisationCampaigns from "@/api/optimisationCampaigns";
import adaptivePolicies from "@/api/adaptivePolicies";
import rlPolicies from "@/api/rlPolicies";

const POLL_MS = 4000;
const RUNNING_STATUSES = new Set(["queued", "running"]);

const ConvergenceChart = lazy(() => import("@/components/optimisation/ConvergenceChart"));
const Convergence3D = lazy(() => import("@/components/optimisation/Convergence3D"));

function _formatNum(v, digits = 3) {
    if (v === null || v === undefined || Number.isNaN(v)) return "—";
    if (typeof v !== "number") return String(v);
    return Number(v).toFixed(digits);
}

function _statusClasses(s) {
    if (s === "complete") return "border-emerald-500/40 bg-emerald-500/10 text-emerald-300";
    if (s === "running") return "border-blue-500/40 bg-blue-500/10 text-blue-300";
    if (s === "queued") return "border-amber-500/40 bg-amber-500/10 text-amber-200";
    if (s === "failed") return "border-red-500/40 bg-red-500/10 text-red-300";
    if (s === "cancelled") return "border-ux-border-1 bg-ux-surface-2/40 text-ux-text-3";
    return "border-ux-border-1 bg-ux-surface-2/40 text-ux-text-3";
}

export default function OptimisationCampaignDetailPage() {
    const { campaignId } = useParams();
    const [campaign, setCampaign] = useState(null);
    const [error, setError] = useState(null);
    const [cancelling, setCancelling] = useState(false);
    const [adaptive, setAdaptive] = useState(null);
    const [adaptiveBusy, setAdaptiveBusy] = useState(false);
    const [rl, setRL] = useState(null);
    const [rlBusy, setRLBusy] = useState(false);
    const [reportBusy, setReportBusy] = useState(false);
    const pollRef = useRef(null);

    const refresh = async () => {
        try {
            const data = await optimisationCampaigns.get(campaignId);
            setCampaign(data);
            setError(null);
        } catch (e) {
            setError(e?.response?.data?.detail || e?.message || "Fetch failed.");
        }
    };

    const refreshSatellites = async () => {
        try {
            const [aRes, rRes] = await Promise.allSettled([
                adaptivePolicies.list(campaignId),
                rlPolicies.list(campaignId),
            ]);
            if (aRes.status === "fulfilled") {
                const items = aRes.value?.items || [];
                setAdaptive(items[0] || null);
            }
            if (rRes.status === "fulfilled") {
                const items = rRes.value?.items || [];
                setRL(items[0] || null);
            }
        } catch (e) {
            // best-effort; don't block the page
        }
    };

    useEffect(() => {
        refresh();
        refreshSatellites();
        return () => {
            if (pollRef.current) clearTimeout(pollRef.current);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [campaignId]);

    useEffect(() => {
        if (!campaign) return undefined;
        if (RUNNING_STATUSES.has(campaign.status)) {
            pollRef.current = setTimeout(refresh, POLL_MS);
            return () => clearTimeout(pollRef.current);
        }
        return undefined;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [campaign]);

    const onCancel = async () => {
        if (!campaign) return;
        setCancelling(true);
        try {
            await optimisationCampaigns.cancel(campaign.id);
            await refresh();
        } catch (e) {
            setError(e?.response?.data?.detail || e?.message || "Cancel failed.");
        } finally {
            setCancelling(false);
        }
    };

    const onTrainAdaptive = async () => {
        if (!campaign) return;
        setAdaptiveBusy(true);
        try {
            const trained = await adaptivePolicies.train({
                source_campaign_id: campaign.id,
                k: 3,
                top_n_elites: 8,
            });
            const evaluated = await adaptivePolicies.evaluate(trained.id, {
                n_holdout: 6,
                seeds_per_holdout: 5,
            });
            setAdaptive(evaluated);
        } catch (e) {
            setError(e?.response?.data?.detail || e?.message || "Adaptive train failed.");
        } finally {
            setAdaptiveBusy(false);
        }
    };

    const onTrainRL = async () => {
        if (!campaign) return;
        setRLBusy(true);
        try {
            const trained = await rlPolicies.train({
                source_campaign_id: campaign.id,
                n_steps: 200,
                mc_seeds_per_step: 4,
                learning_rate: 0.05,
            });
            setRL(trained);
        } catch (e) {
            setError(e?.response?.data?.detail || e?.message || "RL train failed.");
        } finally {
            setRLBusy(false);
        }
    };

    const onDownloadReport = async () => {
        if (!campaign) return;
        setReportBusy(true);
        try {
            const resp = await client.get(
                `/api/optimisation-campaigns/${campaign.id}/report.pdf`,
                { responseType: "blob" },
            );
            const url = window.URL.createObjectURL(new Blob([resp.data], {
                type: "application/pdf",
            }));
            const a = document.createElement("a");
            a.href = url;
            a.download = `optimisation_${campaign.id}.pdf`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            window.URL.revokeObjectURL(url);
        } catch (e) {
            setError(e?.response?.data?.detail || e?.message || "Report failed.");
        } finally {
            setReportBusy(false);
        }
    };

    const decisionVarsByCategory = useMemo(() => {
        const acc = {};
        const vars = campaign?.decision_variables || [];
        for (const v of vars) {
            const cat = v.category || "uncategorised";
            if (!acc[cat]) acc[cat] = [];
            acc[cat].push(v);
        }
        return acc;
    }, [campaign?.decision_variables]);

    if (!campaign && !error) {
        return (
            <div
                className="flex min-h-[40vh] items-center justify-center text-sm text-ux-text-3"
                data-testid="optimisation-detail-loading"
            >
                <Loader2 className="mr-2 size-4 animate-spin" /> Loading campaign…
            </div>
        );
    }
    if (error && !campaign) {
        return (
            <Alert variant="destructive" data-testid="optimisation-detail-error">
                <AlertTriangle className="size-4" />
                <AlertDescription>{String(error)}</AlertDescription>
            </Alert>
        );
    }

    const c = campaign;
    const kpis = c.kpis || {};
    const progress = c.progress || {};
    const trace = Array.isArray(c.convergence_trace) ? c.convergence_trace : [];

    return (
        <div className="space-y-6" data-testid="optimisation-campaign-detail-page">
            {c.legacy && (
                <div
                    className="rounded-md border border-amber-500/40 bg-amber-500/10 p-3 text-xs text-amber-200"
                    data-testid="optimisation-detail-legacy-banner"
                >
                    <strong>LEGACY — read only.</strong> This optimisation
                    campaign was created before the unified workbench
                    and is preserved for archival viewing only. Edit, clone,
                    re-run, train satellite policies, and delete affordances
                    are disabled. Create a new unified campaign at{" "}
                    <Link to="/campaigns/new" className="underline">/campaigns/new</Link>.
                </div>
            )}
            <PageHeader
                title={scrubPhaseLabel(c.name || c.id)}
                subtitle={c.scenario_name || c.scenario_id}
                actions={[
                    <Link
                        key="back"
                        to="/campaigns"
                        className="inline-flex items-center gap-1 text-xs underline text-ux-text-3"
                        data-testid="optimisation-detail-back-link"
                    >
                        <ArrowLeft className="size-3" /> Back to all campaigns
                    </Link>,
                    RUNNING_STATUSES.has(c.status) && !c.legacy ? (
                        <Button
                            key="cancel"
                            variant="ghost"
                            size="sm"
                            onClick={onCancel}
                            disabled={cancelling}
                            data-testid="optimisation-detail-cancel-button"
                            className="gap-1 text-amber-300"
                        >
                            <X className="size-3" />
                            {cancelling ? "Cancelling…" : "Cancel run"}
                        </Button>
                    ) : null,
                ]}
            />

            {error && (
                <Alert variant="destructive" data-testid="optimisation-detail-banner-error">
                    <AlertTriangle className="size-4" />
                    <AlertDescription>{String(error)}</AlertDescription>
                </Alert>
            )}

            {/* KPI strip */}
            <div
                className="grid grid-cols-2 gap-3 sm:grid-cols-4"
                data-testid="optimisation-detail-kpi-strip"
            >
                <div className="rounded-md border border-ux-border-1 bg-ux-surface-1 p-3">
                    <p className="text-[10px] uppercase tracking-wider text-ux-text-3">Status</p>
                    <p
                        className={[
                            "mt-1 inline-block rounded-full border px-2 py-0.5 text-xs uppercase",
                            _statusClasses(c.status),
                        ].join(" ")}
                        data-testid="optimisation-detail-status-badge"
                    >
                        {c.status}
                    </p>
                </div>
                <div className="rounded-md border border-ux-border-1 bg-ux-surface-1 p-3">
                    <p className="text-[10px] uppercase tracking-wider text-ux-text-3">Best Pk</p>
                    <p className="mt-1 text-lg font-medium text-ux-text-1" data-testid="optimisation-detail-best-pk">
                        {_formatNum(kpis.best_pk)}
                    </p>
                </div>
                <div className="rounded-md border border-ux-border-1 bg-ux-surface-1 p-3">
                    <p className="text-[10px] uppercase tracking-wider text-ux-text-3">Baseline Pk</p>
                    <p className="mt-1 text-lg font-medium text-ux-text-1" data-testid="optimisation-detail-baseline-pk">
                        {_formatNum(kpis.baseline_pk)}
                    </p>
                </div>
                <div className="rounded-md border border-ux-border-1 bg-ux-surface-1 p-3">
                    <p className="text-[10px] uppercase tracking-wider text-ux-text-3">Pk Δ</p>
                    <p className="mt-1 text-lg font-medium text-ux-text-1" data-testid="optimisation-detail-pk-delta">
                        {kpis.pk_delta === null || kpis.pk_delta === undefined
                            ? "—"
                            : (kpis.pk_delta > 0 ? "+" : "") + _formatNum(kpis.pk_delta)}
                    </p>
                </div>
            </div>

            {/* GA progress chip */}
            {RUNNING_STATUSES.has(c.status) && (
                <div
                    className="rounded-md border border-blue-500/30 bg-blue-500/10 p-3 text-xs text-blue-200"
                    data-testid="optimisation-detail-progress-chip"
                >
                    <Loader2 className="mr-2 inline size-3 animate-spin" />
                    Generation{" "}
                    <strong>{progress.generation ?? 0}</strong>
                    {" / "}
                    <strong>{progress.total_generations ?? "?"}</strong>{" "}
                    · best Pk so far <strong>{_formatNum(progress.best_pk_so_far)}</strong>
                </div>
            )}

            {/* Convergence chart */}
            <section
                className="rounded-md border border-ux-border-1 bg-ux-surface-1 p-4"
                data-testid="optimisation-detail-convergence-card"
            >
                <h2 className="text-sm font-medium text-ux-text-1">Convergence trace</h2>
                <p className="text-xs text-ux-text-3">
                    Best and mean Pk per generation. Higher is better.
                </p>
                {trace.length > 0 ? (
                    <Suspense
                        fallback={
                            <div
                                className="mt-3 flex h-64 items-center justify-center text-xs text-ux-text-3"
                                data-testid="optimisation-detail-convergence-fallback"
                            >
                                <Loader2 className="mr-2 size-3 animate-spin" /> Loading chart…
                            </div>
                        }
                    >
                        <ConvergenceChart trace={trace} />
                    </Suspense>
                ) : (
                    <div
                        className="mt-3 flex h-64 items-center justify-center text-xs text-ux-text-3"
                        data-testid="optimisation-detail-convergence-empty"
                    >
                        No convergence data yet.
                    </div>
                )}
            </section>

            {/* Best individual table */}
            <section
                className="rounded-md border border-ux-border-1 bg-ux-surface-1 p-4"
                data-testid="optimisation-detail-best-card"
            >
                <h2 className="text-sm font-medium text-ux-text-1">Best individual</h2>
                {c.best_individual ? (
                    <div className="mt-3 space-y-3">
                        <div className="flex flex-wrap gap-3 text-xs text-ux-text-3">
                            <span data-testid="optimisation-detail-best-summary-pk">
                                Pk: <strong className="text-ux-text-1">{_formatNum(c.best_individual.pk)}</strong>
                            </span>
                            <span data-testid="optimisation-detail-best-summary-miss">
                                Mean miss: <strong className="text-ux-text-1">
                                    {c.best_individual.miss_mean_m === null || c.best_individual.miss_mean_m === undefined
                                        ? "—" : `${_formatNum(c.best_individual.miss_mean_m, 1)} m`}
                                </strong>
                            </span>
                            <span data-testid="optimisation-detail-best-summary-evals">
                                Evaluated on: <strong className="text-ux-text-1">{c.best_individual.n_evaluated}</strong> seeds
                            </span>
                        </div>
                        {Object.entries(decisionVarsByCategory).map(([cat, vars]) => (
                            <div key={cat}>
                                <h3
                                    className="text-[10px] uppercase tracking-wider text-ux-text-3"
                                    data-testid={`optimisation-detail-best-cat-${cat}`}
                                >
                                    {cat.replace(/_/g, " ")}
                                </h3>
                                <ul className="mt-1 space-y-1 text-xs">
                                    {vars.map((v) => {
                                        const val = c.best_individual?.gene?.[v.id];
                                        return (
                                            <li
                                                key={v.id}
                                                className="grid grid-cols-[1fr_auto_auto] gap-3 rounded bg-ux-surface-2/40 px-2 py-1"
                                                data-testid={`optimisation-detail-best-row-${v.id}`}
                                            >
                                                <span className="text-ux-text-2">{v.id}</span>
                                                <span className="font-mono text-ux-text-1">
                                                    {_formatNum(val, 3)}
                                                </span>
                                                <span className="font-mono text-[10px] text-ux-text-3">
                                                    [{_formatNum(v.lo, 1)}, {_formatNum(v.hi, 1)}] {v.unit}
                                                </span>
                                            </li>
                                        );
                                    })}
                                </ul>
                            </div>
                        ))}
                    </div>
                ) : (
                    <p
                        className="mt-3 text-xs text-ux-text-3"
                        data-testid="optimisation-detail-best-empty"
                    >
                        No best individual yet — campaign is still {c.status}.
                    </p>
                )}
            </section>

            {/* 3D viewer (Phase 15.4 — "the fourth 3D") */}
            <section
                className="rounded-md border border-ux-border-1 bg-ux-surface-1 p-4"
                data-testid="optimisation-detail-convergence-3d-card"
            >
                <div className="flex items-center justify-between">
                    <div>
                        <h2 className="text-sm font-medium text-ux-text-1">
                            Convergence trace — 3D
                        </h2>
                        <p className="text-xs text-ux-text-3">
                            Best individual per generation projected onto three
                            decision-variable axes; markers coloured by Pk.
                        </p>
                    </div>
                </div>
                <Suspense
                    fallback={
                        <div
                            className="mt-3 flex h-72 items-center justify-center text-xs text-ux-text-3"
                            data-testid="optimisation-detail-convergence-3d-fallback"
                        >
                            <Loader2 className="mr-2 size-3 animate-spin" /> Loading 3D viewer…
                        </div>
                    }
                >
                    <Convergence3D campaign={c} />
                </Suspense>
                {/* Phase 15.4 placeholder testid retained for backwards compat */}
                <div data-testid="optimisation-detail-convergence-3d-card-placeholder" hidden />
            </section>

            {/* Adaptive policy (Phase 15.2) */}
            <section
                className="rounded-md border border-ux-border-1 bg-ux-surface-1 p-4"
                data-testid="optimisation-detail-adaptive-card"
            >
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                        <Brain className="size-4 text-ux-text-3" />
                        <h2 className="text-sm font-medium text-ux-text-1">Adaptive policy</h2>
                    </div>
                    {c.status === "complete" && !c.legacy && (
                        <Button
                            variant="ghost" size="sm"
                            onClick={onTrainAdaptive}
                            disabled={adaptiveBusy}
                            data-testid="optimisation-detail-adaptive-train-button"
                        >
                            {adaptiveBusy
                                ? <><Loader2 className="mr-1 size-3 animate-spin" /> Training…</>
                                : adaptive ? "Retrain" : "Train + evaluate"}
                        </Button>
                    )}
                </div>
                {adaptive ? (
                    <div className="mt-2 grid grid-cols-3 gap-3 text-xs" data-testid="optimisation-detail-adaptive-summary">
                        <div>
                            <p className="text-[10px] uppercase tracking-wider text-ux-text-3">Baseline Pk (holdout)</p>
                            <p className="font-mono text-ux-text-1" data-testid="optimisation-detail-adaptive-baseline">
                                {_formatNum(adaptive?.holdout_evaluation?.baseline_pk_mean)}
                            </p>
                        </div>
                        <div>
                            <p className="text-[10px] uppercase tracking-wider text-ux-text-3">Adaptive Pk (holdout)</p>
                            <p className="font-mono text-ux-text-1" data-testid="optimisation-detail-adaptive-adaptive">
                                {_formatNum(adaptive?.holdout_evaluation?.adaptive_pk_mean)}
                            </p>
                        </div>
                        <div>
                            <p className="text-[10px] uppercase tracking-wider text-ux-text-3">Pk uplift</p>
                            <p className="font-mono text-ux-text-1" data-testid="optimisation-detail-adaptive-uplift">
                                {(() => {
                                    const u = adaptive?.holdout_evaluation?.pk_uplift_mean;
                                    if (u === null || u === undefined) return "—";
                                    return (u > 0 ? "+" : "") + _formatNum(u);
                                })()}
                            </p>
                        </div>
                    </div>
                ) : (
                    <p className="mt-2 text-xs text-ux-text-3">
                        Train a k-NN policy from the campaign's elite individuals and
                        evaluate it against random holdout launch geometries.
                    </p>
                )}
            </section>

            {/* RL policy (Phase 15.3) */}
            <section
                className="rounded-md border border-ux-border-1 bg-ux-surface-1 p-4"
                data-testid="optimisation-detail-rl-card"
            >
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                        <Zap className="size-4 text-ux-text-3" />
                        <h2 className="text-sm font-medium text-ux-text-1">Narrow RL policy</h2>
                    </div>
                    {c.status === "complete" && !c.legacy && (
                        <Button
                            variant="ghost" size="sm"
                            onClick={onTrainRL}
                            disabled={rlBusy}
                            data-testid="optimisation-detail-rl-train-button"
                        >
                            {rlBusy
                                ? <><Loader2 className="mr-1 size-3 animate-spin" /> Training…</>
                                : rl ? "Retrain" : "Train (200 steps)"}
                        </Button>
                    )}
                </div>
                {rl ? (
                    <div className="mt-2 grid grid-cols-3 gap-3 text-xs" data-testid="optimisation-detail-rl-summary">
                        <div>
                            <p className="text-[10px] uppercase tracking-wider text-ux-text-3">Baseline reward</p>
                            <p className="font-mono text-ux-text-1" data-testid="optimisation-detail-rl-baseline">
                                {_formatNum(rl.baseline_reward)}
                            </p>
                        </div>
                        <div>
                            <p className="text-[10px] uppercase tracking-wider text-ux-text-3">Final reward</p>
                            <p className="font-mono text-ux-text-1" data-testid="optimisation-detail-rl-final">
                                {_formatNum(rl.final_reward)}
                            </p>
                        </div>
                        <div>
                            <p className="text-[10px] uppercase tracking-wider text-ux-text-3">Reward uplift</p>
                            <p className="font-mono text-ux-text-1" data-testid="optimisation-detail-rl-uplift">
                                {(() => {
                                    const u = rl.reward_uplift;
                                    if (u === null || u === undefined) return "—";
                                    return (u > 0 ? "+" : "") + _formatNum(u);
                                })()}
                            </p>
                        </div>
                    </div>
                ) : (
                    <p className="mt-2 text-xs text-ux-text-3">
                        Hand-rolled REINFORCE policy gradient (numpy) over the 5-D
                        guidance-gain perturbation. Single fixed scenario.
                    </p>
                )}
            </section>

            {/* PDF report download (Phase 15.5) */}
            <section
                className="rounded-md border border-ux-border-1 bg-ux-surface-1 p-4"
                data-testid="optimisation-detail-pdf-card"
            >
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                        <FileDown className="size-4 text-ux-text-3" />
                        <h2 className="text-sm font-medium text-ux-text-1">PDF report</h2>
                    </div>
                    <Button
                        variant="ghost" size="sm"
                        onClick={onDownloadReport}
                        disabled={reportBusy || c.status === "running" || c.status === "queued"}
                        data-testid="optimisation-detail-pdf-download-button"
                    >
                        {reportBusy
                            ? <><Loader2 className="mr-1 size-3 animate-spin" /> Rendering…</>
                            : "Download PDF"}
                    </Button>
                </div>
                <p className="mt-1 text-xs text-ux-text-3">
                    Includes KPI strip, 2D + 3D convergence charts, best-individual table,
                    and (when trained) adaptive + RL satellite policy summaries.
                </p>
            </section>
        </div>
    );
}
